import {
  Box,
  Button,
  ButtonProps,
  Divider,
  Drawer,
  DrawerProps,
  makeStyles,
  Theme,
  Tooltip,
  TooltipProps,
  TypographyProps,
} from '@material-ui/core';
import React, { ReactNode } from 'react';
import DrawerActions from '../DrawerActions';
import DrawerContent, { DrawerContentProps } from '../DrawerContent';
import DrawerTitle from '../DrawerTitle';

export type FormDrawerProps = DrawerProps & {
  children?: ReactNode;
  alert?: ReactNode;
  titleProps?: TypographyProps;
  header?: string;
  headerChildren?: ReactNode;
  headerClassName?: string;
  footerChildren?: ReactNode;
  width?: number | string;
  onDismissAction?: () => void;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  onTertiaryAction?: () => void;
  primaryActionLabel?: string | ReactNode;
  secondaryActionLabel?: string | ReactNode;
  tertiaryActionLabel?: string | ReactNode;
  primaryActionProps?: ButtonProps & { testId?: string };
  dismissActionProps?: ButtonProps & { testId?: string };
  secondaryActionProps?: ButtonProps & { testId?: string };
  SecondaryActionComponent?: JSX.Element;
  tertiaryActionProps?: ButtonProps & { testId?: string };
  drawerContentProps?: DrawerContentProps;
  testId?: string;
  loading?: boolean;
  useBackdropDismiss?: boolean;
  useKeyboardEscape?: boolean;
  divider?: boolean;
  hideSecondaryAction?: boolean;
  caption?: ReactNode;
  primaryActionTooltip?: {
    placement: TooltipProps['placement'];
    title: string;
  };
};
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: '100vw',
  },
  buttonsContainer: {
    [theme.breakpoints.only('xs')]: {
      minHeight: 'auto',
      flexDirection: 'column',
      display: 'flex',
      paddingBottom: theme.spacing(0),
    },
    [theme.breakpoints.up('sm')]: {
      '& > :first-child': {
        marginLeft: theme.spacing(2),
      },
      display: 'flex',
      flexDirection: 'row-reverse',
    },
  },
  secondaryButton: {
    display: 'flex',
    alignSelf: 'center',
    [theme.breakpoints.up('sm')]: {
      flex: 1,
    },
  },
  dividerSpacing: {
    marginBottom: theme.spacing(4),
  },
  body: {
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(0),
  },
}));

export const FormDrawer = ({
  alert,
  titleProps,
  header,
  headerChildren,
  headerClassName,
  footerChildren,
  children,
  width = 600,
  onPrimaryAction = () => undefined,
  onDismissAction = () => undefined,
  onSecondaryAction = () => undefined,
  onTertiaryAction = () => undefined,
  primaryActionLabel,
  secondaryActionLabel,
  tertiaryActionLabel,
  dismissActionProps: dismissActionPropsBase,
  primaryActionProps: primaryActionPropsBase,
  secondaryActionProps: secondaryActionPropsBase,
  SecondaryActionComponent,
  tertiaryActionProps: tertiaryActionPropsBase,
  drawerContentProps,
  testId = 'form-drawer' || null,
  loading = false,
  useBackdropDismiss = false,
  useKeyboardEscape = true,
  divider,
  hideSecondaryAction = false,
  caption,
  primaryActionTooltip,
  ...props
}: FormDrawerProps): JSX.Element => {
  const classes = useStyles();

  const { testId: primaryActionTestId, ...primaryActionProps } = primaryActionPropsBase || {};
  const { testId: secondaryActionTestId, ...secondaryActionProps } = secondaryActionPropsBase || {};
  const { testId: tertiaryActionTestId, ...tertiaryActionProps } = tertiaryActionPropsBase || {};
  const { testId: dismissActionTestId, ...dismissActionProps } = dismissActionPropsBase || {};
  const primaryAction = (
    <Button
      onClick={onPrimaryAction}
      id="primary-action"
      name="primary-action"
      color="primary"
      size="large"
      data-testid={primaryActionTestId}
      {...primaryActionProps}
    >
      {primaryActionLabel}
    </Button>
  );

  return (
    <Drawer
      disableBackdropClick={!useBackdropDismiss}
      disableEscapeKeyDown={!useKeyboardEscape}
      onClose={onDismissAction}
      anchor="right"
      {...props}
    >
      <Box style={{ width }} className={classes.root} data-testid={testId}>
        <DrawerTitle title={header} titleProps={titleProps} className={headerClassName} caption={caption}>
          {headerChildren}
        </DrawerTitle>
        {divider && <Divider className={classes.dividerSpacing} />}
        <DrawerContent {...drawerContentProps}>{children}</DrawerContent>
        <DrawerActions loading={loading} alert={alert}>
          {footerChildren}
          {tertiaryActionLabel && (
            <div className={classes.secondaryButton}>
              <Button
                id="tertiary-action"
                variant="outlined"
                color="primary"
                size="large"
                onClick={onTertiaryAction}
                data-testid={tertiaryActionTestId}
                {...tertiaryActionProps}
              >
                {tertiaryActionLabel}
              </Button>
            </div>
          )}
          {SecondaryActionComponent && <div className={classes.secondaryButton}>{SecondaryActionComponent}</div>}
          {secondaryActionLabel && (
            <div className={classes.secondaryButton}>
              <Button
                id="secondary-action"
                variant="text"
                color="primary"
                size="large"
                onClick={onSecondaryAction}
                data-testid={secondaryActionTestId}
                {...secondaryActionProps}
              >
                {secondaryActionLabel}
              </Button>
            </div>
          )}
          <div className={classes.buttonsContainer}>
            {primaryActionTooltip ? (
              <Tooltip arrow placement={primaryActionTooltip.placement} title={primaryActionTooltip.title}>
                <span>{primaryAction}</span>
              </Tooltip>
            ) : (
              primaryAction
            )}
            {!hideSecondaryAction && (
              <Button
                onClick={onDismissAction}
                id="dismiss-action"
                color="secondary"
                variant="outlined"
                size="large"
                data-testid={dismissActionTestId}
                {...dismissActionProps}
              >
                Cancel
              </Button>
            )}
          </div>
        </DrawerActions>
      </Box>
    </Drawer>
  );
};
export default FormDrawer;
